/**
 * Locations Management Page
 * Manage business locations and operating hours
 */

import { useState, useEffect } from 'react';
import { PlusIcon } from '@heroicons/react/24/outline';
import { adminAPI } from '../../services/api';
import LocationCard from '../../components/admin/LocationCard';
import LocationForm from '../../components/admin/LocationForm';
import LocationHoursEditor from '../../components/admin/LocationHoursEditor';

export default function LocationsManagement() {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showLocationForm, setShowLocationForm] = useState(false);
  const [showHoursEditor, setShowHoursEditor] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);

  useEffect(() => {
    fetchLocations();
  }, []);

  const fetchLocations = async () => {
    try {
      setLoading(true);
      const { data } = await adminAPI.locations.getAll();
      setLocations(data.locations || []);
      setError('');
    } catch (err) {
      console.error('Error fetching locations:', err);
      setError(err.response?.data?.error || 'Failed to load locations');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (locationId) => {
    if (!window.confirm('Are you sure you want to delete this location?')) {
      return;
    }

    try {
      await adminAPI.locations.delete(locationId);
      fetchLocations();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete location');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading locations...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Locations</h1>
          <p className="text-gray-600">Manage business locations and hours</p>
        </div>
        <button
          onClick={() => {
            setSelectedLocation(null);
            setShowLocationForm(true);
          }}
          className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 flex items-center"
        >
          <PlusIcon className="w-5 h-5 mr-2" />
          Add Location
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg mb-6">
          {error}
        </div>
      )}

      {locations.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm p-12 text-center">
          <h3 className="text-lg font-medium text-gray-900 mb-2">No locations found</h3>
          <p className="text-gray-500 mb-6">Add your first business location to get started</p>
          <button
            onClick={() => setShowLocationForm(true)}
            className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700"
          >
            Add Location
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {locations.map(location => (
            <LocationCard
              key={location.id}
              location={location}
              onEdit={(loc) => {
                setSelectedLocation(loc);
                setShowLocationForm(true);
              }}
              onEditHours={(loc) => {
                setSelectedLocation(loc);
                setShowHoursEditor(true);
              }}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}

      {/* Location Form Modal */}
      {showLocationForm && (
        <LocationForm
          location={selectedLocation}
          onClose={() => {
            setShowLocationForm(false);
            setSelectedLocation(null);
          }}
          onSuccess={fetchLocations}
        />
      )}

      {/* Hours Editor Modal */}
      {showHoursEditor && (
        <LocationHoursEditor
          location={selectedLocation}
          onClose={() => {
            setShowHoursEditor(false);
            setSelectedLocation(null);
          }}
          onSuccess={fetchLocations}
        />
      )}
    </div>
  );
}
